import { useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Agreement, fetchAgreements } from "@/networking/agreements.api";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";

function LinksLoader() {
  return (
    <div className="flex animate-pulse gap-6">
      <div className="h-4 w-24 rounded bg-gray-200" />
      <div className="h-4 w-24 rounded bg-gray-200" />
    </div>
  );
}

type Props = {
  isLoading: boolean;
  companyName?: string;
  agreements?: Agreement[];
  className?: string;
};

export default function Footer({
  isLoading,
  companyName,
  agreements = [],
  className,
}: Props) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const fetchAgreementMutation = useMutation({
    mutationFn: (id: string) => fetchAgreements(id),
    onError: (error) => {
      console.error("Error fetching agreement", error);
    },
  });

  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(
    null,
  );
  const [isWrapped, setIsWrapped] = useState(false);
  const measureRef = useRef<HTMLDivElement>(null);
  const agreementData = fetchAgreementMutation.data;
  const isLoadingAgreement = fetchAgreementMutation.isPending;

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    const check = () => {
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length < 2) {
        setIsWrapped(false);
        return;
      }
      const firstTop = children[0]!.offsetTop;
      setIsWrapped(children.some((child) => child.offsetTop > firstTop));
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [agreements.length, isLoading]);

  return (
    <footer
      className={clsx(
        "relative z-20 border-t border-gray-200 bg-white",
        "py-3",
        className,
      )}
    >
      <div className="w-full px-0">
        {isLoading ? (
          <div className="flex justify-center">
            <LinksLoader />
          </div>
        ) : (
          <>
            {/* Permanently hidden clone — absolute so it doesn't take layout space */}
            <div
              ref={measureRef}
              aria-hidden
              className="pointer-events-none invisible absolute right-0 left-0 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs text-gray-500"
            >
              {agreements.map((agreement) => (
                <span key={agreement.id}>{agreement.title}</span>
              ))}
              <span>
                ©{year} {companyName || ""}
              </span>
            </div>

            {!isWrapped && (
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs text-gray-500">
                {agreements.map((agreement) => (
                  <span
                    key={agreement.id}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer underline hover:text-black"
                    onClick={() => {
                      if (!agreement.id) return;
                      const id = agreement.id.toString();
                      setSelectedAgreementId(id);
                      fetchAgreementMutation.mutate(id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!agreement.id) return;
                        const id = agreement.id.toString();
                        setSelectedAgreementId(id);
                        fetchAgreementMutation.mutate(id);
                      }
                    }}
                  >
                    {agreement.title}
                  </span>
                ))}
                <span>
                  ©{year} {companyName || ""}
                </span>
              </div>
            )}

            {isWrapped && (
              <p className="text-center text-xs text-gray-500">
                {agreements.map((agreement, index) => (
                  <span key={agreement.id}>
                    <span
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer underline hover:text-black"
                      onClick={() => {
                        if (!agreement.id) return;
                        const id = agreement.id.toString();
                        setSelectedAgreementId(id);
                        fetchAgreementMutation.mutate(id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (!agreement.id) return;
                          const id = agreement.id.toString();
                          setSelectedAgreementId(id);
                          fetchAgreementMutation.mutate(id);
                        }
                      }}
                    >
                      {agreement.title}
                    </span>
                    {index < agreements.length - 1 ? ", " : " · "}
                  </span>
                ))}
                <span>
                  ©{year} {companyName || ""}
                </span>
              </p>
            )}
          </>
        )}

        <Dialog
          open={selectedAgreementId !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedAgreementId(null);
          }}
        >
          <DialogContent className="h-[80vh] overflow-y-auto sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {
                  agreements.find(
                    (a) => a.id?.toString() === selectedAgreementId,
                  )?.title
                }
              </DialogTitle>
              {isLoadingAgreement ? (
                <div className="flex h-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-2 border-t-gray-500 border-r-gray-500" />
                </div>
              ) : (
                <iframe
                  srcDoc={agreementData?.agreement.description || ""}
                  className="h-full w-full border-none"
                  title={t("agreement_content")}
                />
              )}
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
    </footer>
  );
}
